// Merchant active orders — anything paid that hasn't been confirmed/cancelled.
//
// Until Phase 2 wires Stripe + the fulfillment state machine, this list
// will mostly be empty; included now so the menu item is real.

import { Fragment, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Loader2, MapPin, Clock, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, EmptyState } from '../../components/shared/primitives';
import { DeliveryProof } from '../../components/shared/DeliveryProof';
import { Button } from '../../components/stripe-ui/Button';
import { MERCHANT_NAV } from '../_navs';
import { requests, receipts, delays, type ServiceRequestWithItems, type CertLang } from '../../lib/api';
import { formatHtgMinor } from '../../lib/money';

const ACTIVE_FULFILLMENT = ['paid', 'assigned_to_merchant', 'in_progress', 'ready_for_delivery', 'delivered', 'confirmed'];

// fulfillment.status -> the merchant's next-action button label.
const NEXT_ACTION: Record<string, string> = {
  not_started: 'Start preparing',
  preparing: 'Mark ready',
  ready: 'Out for delivery',
  out_for_delivery: 'Mark delivered',
};
const FULFILL_LABEL: Record<string, string> = {
  not_started: 'Paid · not started', preparing: 'Preparing', ready: 'Ready',
  out_for_delivery: 'Out for delivery', delivered: 'Delivered · awaiting confirm', confirmed: 'Confirmed',
};

export function MerchantOrdersPage() {
  const q = useQuery({ queryKey: ['merchant', 'orders'], queryFn: () => requests.list() });
  const orders = useMemo(() => (q.data ?? []).filter((r) => ACTIVE_FULFILLMENT.includes(r.status)), [q.data]);

  return (
    <PortalShell
      nav={MERCHANT_NAV}
      activeId="orders"
      title="Active orders"
      subtitle="Paid orders to prepare and deliver."
    >
      {q.isLoading && (
        <Card><div className="text-center py-12 text-[13px] text-slate-400"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div></Card>
      )}
      {!q.isLoading && orders.length === 0 && (
        <Card>
          <EmptyState
            icon={Package}
            title="No active orders"
            body="Once a customer pays a quote, the order shows up here. Stripe + the fulfillment state machine ship in Phase 2."
          />
        </Card>
      )}
      {orders.length > 0 && (
        <Card padding="p-0">
          <div className="p-5 border-b border-slate-100">
            <SectionHeader title={`${orders.length} active`} subtitle="Paid; awaiting preparation, delivery, or recipient confirmation." />
          </div>
          <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
            <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
              <tr>
                <th className="text-left py-2.5 px-5 font-medium">Order</th>
                <th className="text-left py-2.5 px-3 font-medium">Recipient</th>
                <th className="text-left py-2.5 px-3 font-medium">City</th>
                <th className="text-left py-2.5 px-3 font-medium">Fulfillment</th>
                <th className="text-right py-2.5 px-5 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map((r) => <OrderRow key={r.id} r={r} />)}
            </tbody>
          </table>
        </Card>
      )}
    </PortalShell>
  );
}

function OrderRow({ r }: { r: ServiceRequestWithItems }) {
  const qc = useQueryClient();
  const fstatus = r.fulfillment?.status ?? 'not_started';
  const nextLabel = NEXT_ACTION[fstatus];
  const adv = useMutation({
    mutationFn: () => requests.advance(r.id),
    onSuccess: () => { toast.success('Order updated'); qc.invalidateQueries({ queryKey: ['merchant', 'orders'] }); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });
  const showProof = ['out_for_delivery', 'delivered', 'confirmed'].includes(fstatus);
  return (
    <Fragment>
      <tr className="hover:bg-slate-50">
        <td className="py-3 px-5">
          <p className="font-mono text-cyan-700">REQ-{String(r.id).padStart(4, '0')}</p>
          <p className="text-[12px] text-slate-600">{r.category?.label ?? '—'} · {r.cart_item_count} items · {r.cart_subtotal_htg_minor > 0 ? formatHtgMinor(r.cart_subtotal_htg_minor) : '—'}</p>
        </td>
        <td className="py-3 px-3 text-slate-700">{r.recipient?.full_name ?? '—'}</td>
        <td className="py-3 px-3 text-slate-700"><span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" /> {r.city?.name ?? '—'}</span></td>
        <td className="py-3 px-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${fstatus === 'confirmed' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>{FULFILL_LABEL[fstatus] ?? fstatus}</span>
        </td>
        <td className="py-3 px-5 text-right">
          {fstatus === 'confirmed'
            ? <ReceiptButton requestId={r.id} />
            : fstatus === 'delivered'
              ? <AtDoorConfirm requestId={r.id} />
              : nextLabel
                ? <Button size="sm" onClick={() => adv.mutate()} loading={adv.isPending} disabled={adv.isPending}>{nextLabel}</Button>
                : <span className="inline-flex items-center gap-1 text-[11.5px] text-slate-400"><Clock className="w-3 h-3" /> awaiting confirmation</span>}

          {/* Report a delay while the order is still in flight. A stalled
              order the customer cannot explain is where support tickets and
              disputes come from. */}
          {!['delivered', 'confirmed'].includes(fstatus) && <ReportDelay requestId={r.id} />}
        </td>
      </tr>
      {showProof && (
        <tr>
          <td colSpan={5} className="px-5 pb-3 pt-0 bg-slate-50/40 border-b border-slate-100">
            <DeliveryProof requestId={r.id} canUpload />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

// Courier-OTP: at the door, the recipient reads out the code they received and
// the merchant enters it to confirm receipt. (The sender can never confirm.)
function AtDoorConfirm({ requestId }: { requestId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const confirm = useMutation({
    mutationFn: () => requests.confirmReceipt(requestId, pin.trim()),
    onSuccess: () => { toast.success('Receipt confirmed with the recipient'); qc.invalidateQueries({ queryKey: ['merchant', 'orders'] }); },
    onError: (e: Error) => toast.error(e.message || "That code doesn't match"),
  });
  if (!open) return <Button size="sm" tone="outline" onClick={() => setOpen(true)}>Confirm at door</Button>;
  return (
    <div className="inline-flex items-center gap-1.5">
      <input
        value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric" placeholder="code" autoFocus
        onKeyDown={(e) => { if (e.key === 'Enter' && pin.length >= 4) confirm.mutate(); }}
        className="w-24 px-2 py-1.5 text-[15px] font-mono tracking-[0.2em] text-center border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
      />
      <Button size="sm" onClick={() => confirm.mutate()} loading={confirm.isPending} disabled={pin.length < 4}>Confirm</Button>
    </div>
  );
}

function ReceiptButton({ requestId }: { requestId: number }) {
  const [lang, setLang] = useState<CertLang>('en');
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await receipts.download(requestId, lang); }
    catch (e) { toast.error((e as Error).message || 'Download failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center gap-1.5">
      <select value={lang} onChange={(e) => setLang(e.target.value as CertLang)} className="text-[10.5px] border border-slate-200 rounded px-1 py-0.5 bg-white" aria-label="Receipt language">
        <option value="en">EN</option><option value="fr">FR</option><option value="ht">HT</option>
      </select>
      <button onClick={dl} disabled={busy} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-slate-600 hover:text-slate-900">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Receipt
      </button>
    </div>
  );
}

/**
 * Merchant control to say WHY an order is late.
 *
 * Deliberately a short list plus an optional note, not free text alone: a
 * structured reason can be translated, counted and eventually notified over
 * WhatsApp, while "stuck" cannot. The note carries the specifics.
 */
function ReportDelay({ requestId }: { requestId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('road_blocked');
  const [note, setNote] = useState('');

  const report = useMutation({
    mutationFn: () => delays.report(requestId, { reason, note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success('Delay reported — the customer can see it');
      setOpen(false);
      setNote('');
      qc.invalidateQueries({ queryKey: ['requests', requestId, 'delays'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Could not report the delay'),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ml-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-amber-700 hover:underline"
      >
        <AlertTriangle className="w-3 h-3" /> Report delay
      </button>
    );
  }

  return (
    <div className="mt-2 text-left rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
      <select
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-label="Delay reason"
        className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px]"
      >
        <option value="road_blocked">Road blocked</option>
        <option value="fuel_shortage">Fuel shortage</option>
        <option value="out_of_stock">Item out of stock</option>
        <option value="weather">Weather</option>
        <option value="other">Other</option>
      </select>
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={500}
        placeholder="Optional detail for the customer"
        className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px]"
      />
      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" onClick={() => report.mutate()} loading={report.isPending} disabled={report.isPending}>
          Send
        </Button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[11.5px] font-semibold text-slate-500 hover:underline"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
